import { useIntl } from "react-intl";
import { FocusContext, type FocusedElement } from "src/FocusContext";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import type { AutocompletePlace } from "src/api/v1.6/autocomplete";
import { SearchInput } from "src/components/SearchInput/SearchInput";
import color from "src/design-system/tokens/color";
import { useNavigateToHotelsPage } from "src/utils/hooks/useNavigateToHotelsPage";
import useSearch from "src/utils/hooks/useSearch";
import {
  useSearchInput,
  useSearchKeyboard,
} from "src/utils/hooks/useSearchInput";
import messages from "src/components/AutocompleteInput/AutocompleteInput.messages";
import { headerMessages } from "src/domain/TopNav/TopNav.messages";
import { Icon } from "src/design-system/components/Icon/Icon";
import Pin from "src/design-system/icons/Pin.svg?react";

type Props = {
  setFocusToDatePicker: () => void;
};

export function DestinationInput({ setFocusToDatePicker }: Readonly<Props>) {
  const { origin, destination } = useSearch();
  const originCanonical = origin?.canonicalName; // The origin cannot be updated from within the Hotels Screen.
  const { navigateToHotels } = useNavigateToHotelsPage();
  const intl = useIntl();
  const placeholder = intl.formatMessage(messages.destinationPlaceholder);

  const keyboard = useSearchKeyboard();
  const search = useSearchInput(
    {
      id: "destination",
      place: destination,
      onSelect: handleDestinationAutocompleteSelect,
    },
    keyboard,
  );

  function handleDestinationAutocompleteSelect(place: AutocompletePlace) {
    // Hotels search differs to LargeSearchBar: automatically initiate search request when an autocomplete item is clicked.
    sendAnalyticsInteractionEvent({
      category: "Hotels",
      action: "Initiate:hotelListRequest",
      label: "DestinationChanged",
    });
    navigateToHotels({
      originCanonical: originCanonical ?? "Hotels-near",
      destinationCanonical: place?.canonicalName ?? destination?.canonicalName,
    });
    setFocusToDatePicker();

    sendAnalyticsInteractionEvent({
      category: "Hotels",
      action: "Click:destination-input",
      label: "select-option",
    });
  }

  function handleFocusChange(focusedElement: FocusedElement) {
    keyboard.setFocusedElement(focusedElement, search.displayResults.length);
  }

  return (
    <FocusContext
      value={{
        focusedElement: keyboard.focusedElement,
        onFocusChanged: handleFocusChange,
      }}
    >
      <SearchInput
        search={search}
        keyboard={keyboard}
        id="hotelDestination"
        placeholder={placeholder}
        LeftElement={
          <Icon>
            <Pin
              aria-label={intl.formatMessage(headerMessages.where)}
              color={color.icon.primary}
            />
          </Icon>
        }
      />
    </FocusContext>
  );
}
